import { Typography, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Box } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { PurchasedDomain } from "interfaces/domains";
import moment from "moment";
import DomainAddRoundedIcon from "@mui/icons-material/DomainAddRounded";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import Grid3x3RoundedIcon from "@mui/icons-material/Grid3x3Rounded";
import TelegramIcon from "@mui/icons-material/Telegram";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import DeleteIcon from "@mui/icons-material/Delete";
import Profile from "assets/Profile_pic.png";
import useCautionStore from "store/cautionStore";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";

interface UserStats {
  userId: string;
  email: string;
  telegram: string;
  totalDomains: number;
  totalUserSpent: number;
  totalCost: number;
}

interface PurchasedDomainsTabProps {
  domainsArr: PurchasedDomain[];
  page: number;
  totalDomains: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  userStats?: UserStats | null;
}

const TypographyStyled = styled(Typography)(() => {
  return {
    color: "#6D6777",
    fontWeight: 500,
    fontFamily: "var(--cygreRegular), sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
  };
});

const PurchasedDomainsTab: React.FC<PurchasedDomainsTabProps> = ({
  domainsArr,
  totalDomains,
  page,
  handleChangePage,
  userStats,
}) => {
  const { t } = useTranslation();
  const { handleDomainToDeleteChange, openDomainDeleteModal } = useCautionStore();

  const handleDeleteDomainBtnClick = (domainShortInfo: { id: string; name: string }) => {
    handleDomainToDeleteChange(domainShortInfo);
    openDomainDeleteModal();
  };

  return (
    <Box sx={{ width: "100%", overflowY: "auto" }}>
      {userStats && (
        <Box
          sx={{
            backgroundColor: "#F6F7FB",
            borderRadius: "8px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: "24px",
              marginBottom: "24px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <img
                src={Profile}
                alt="user icon"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                }}
              />
              <Box>
                {userStats.email && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "4px",
                    }}
                  >
                    <AlternateEmailIcon sx={{ color: "#6D6777", width: "18px", height: "18px" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                      }}
                    >
                      {userStats.email}
                    </Typography>
                  </Box>
                )}
                {userStats.telegram && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "4px",
                    }}
                  >
                    <TelegramIcon sx={{ color: "#6D6777", width: "18px", height: "18px" }} />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                      }}
                    >
                      {userStats.telegram}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <PermIdentityIcon sx={{ color: "#6D6777", width: "18px", height: "18px" }} />
                  <Typography
                    sx={{
                      fontSize: "10px",
                      fontWeight: "400",
                      fontFamily: "var(--cygreRegular), sans-serif",
                      color: "#6D6777",
                    }}
                  >
                    {userStats.userId}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: "16px",
            }}
          >
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#6D6777",
                  marginBottom: "8px",
                }}
              >
                {t("totalBoughtDomains")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#2E263D",
                }}
              >
                {userStats.totalDomains}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#6D6777",
                  marginBottom: "8px",
                }}
              >
                {t("totalUserSpent")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#2E263D",
                }}
              >
                ${userStats.totalUserSpent.toFixed(2)}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#6D6777",
                  marginBottom: "8px",
                }}
              >
                {t("totalCost") || "Себестоимость для компании"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "500",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  color: "#2E263D",
                }}
              >
                ${userStats.totalCost.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <Table aria-label="simple table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#F6F7FB",
            }}
          >
            <TableCell
              sx={{
                paddingLeft: "36px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
            >
              <TypographyStyled>{t("domain")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("purchaseDate")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("status")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("price")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("userId")}</TypographyStyled>
            </TableCell>
            <TableCell>
              <TypographyStyled>{t("pwaId")}</TypographyStyled>
            </TableCell>
            <TableCell sx={{ borderTopRightRadius: "8px", borderBottomRightRadius: "8px" }}>
              <TypographyStyled sx={{ textAlign: "center" }}>{t("actions")}</TypographyStyled>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {domainsArr?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="subtitle2">{t("noitems")}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            domainsArr.map(({ id, name, createdAt, status, userPrice, defaultPrice, userId, pwaId }) => {
              const formattedDate = moment(createdAt).format("DD.MM.YYYY");

              return (
                <TableRow key={id}>
                  <TableCell
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "36px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <DomainAddRoundedIcon
                        sx={{
                          color: "#6D6777",
                          width: "26px",
                          height: "26px",
                        }}
                      />

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: "400",
                            fontFamily: "var(--cygreRegular), sans-serif",
                            marginBottom: "4px",
                          }}
                        >
                          {name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: "400",
                            fontFamily: "var(--cygreRegular), sans-serif",
                            color: "#6D6777",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          {`id: ${id}`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                      }}
                    >
                      {createdAt ? formattedDate : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "var(--cygreRegular), sans-serif",
                        color: status === "active" ? "#56CA00" : "#F4701B",
                      }}
                    >
                      {status || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                          marginBottom: "2px",
                        }}
                      >
                        {t("price")}: ${userPrice?.toFixed(2) || "0.00"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                          color: "#6D6777",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        {t("defaultPrice")}: ${defaultPrice?.toFixed(2) || "0.00"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <PermIdentityIcon
                        sx={{
                          color: "#6D6777",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                          color: "#6D6777",
                        }}
                      >
                        {userId || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Grid3x3RoundedIcon
                        sx={{
                          color: "#6D6777",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "400",
                          fontFamily: "var(--cygreRegular), sans-serif",
                          color: "#6D6777",
                        }}
                      >
                        {pwaId || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <BootstrapTooltip title={t("delete")} placement="top">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleDeleteDomainBtnClick({ id, name })}
                      >
                        <DeleteIcon sx={{ color: "#6D6777" }} />
                      </Box>
                    </BootstrapTooltip>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={totalDomains}
        rowsPerPage={20}
        page={page}
        labelRowsPerPage={null}
        labelDisplayedRows={() => ""}
        onPageChange={handleChangePage}
        sx={{
          "& .MuiTablePagination-spacer": {
            display: "none",
          },
          "& .MuiTablePagination-toolbar": {
            padding: 0,
            justifyContent: "flex-start",
          },
        }}
      />
    </Box>
  );
};

export default PurchasedDomainsTab;
